'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useTranslation } from '@/hook';

import { AddressSrc, ICoordinate, ILocation } from '@/types';
import { GeoLocationButton, MarkerPin } from '../../../../public/Images';
import AutocompleteComponent from './auto-complete/auto-complete.component';

import { useReverseGeocodeQuery } from '@/hook/geocoder-api-slice';
import Image from 'next/image';
import styles from './styles.module.scss';

const Map = ({
    onSetLocation,
    location,
    addressSrc,
    setAddressSrc,
}: {
    onSetLocation: (location: ILocation) => void;
    location: ILocation;
    addressSrc: AddressSrc;
    setAddressSrc: (src: AddressSrc) => void;
}) => {
    const moreOptions = [
        {
            featureType: 'administrative',
            elementType: 'geometry',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
        {
            featureType: 'administrative.country',
            elementType: 'geometry',
            stylers: [
                {
                    visibility: 'simplified',
                },
            ],
        },
        {
            featureType: 'poi',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
        {
            featureType: 'road',
            elementType: 'labels.icon',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
        {
            featureType: 'transit',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
    ];

    const defaultMapOptions: google.maps.MapOptions = {
        fullscreenControl: false,
        mapTypeControl: false,
        minZoom: 10,
        maxZoom: 20,
        streetViewControl: false,
        gestureHandling: 'greedy',
        zoomControl: true,
        zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER,
        },
        styles: [...moreOptions],
    };

    const { i18n } = useTranslation();
    const refMap = useRef<google.maps.Map | null>(null);
    const [currCoords, setCurrCoords] = useState<ICoordinate>(
        location.coordinate
    );
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const { data, isLoading, isSuccess } = useReverseGeocodeQuery(
        {
            latLng: currCoords || { lat: 30.0444, lng: 31.2357 },
            lang: i18n.language,
        },
        { skip: addressSrc === 'autocomplete' || !isChanged }
    );
    useEffect(() => {
        if (!isLoading && data && isSuccess && refMap?.current) {
            const currCenter = refMap.current.getCenter()?.toJSON();
            onSetLocation({
                coordinate: currCenter || location.coordinate,
                address: data?.formatted_address,
                city: data.address_components?.filter(
                    item =>
                        item?.types?.includes('locality') ||
                        item?.types?.includes('administrative_area_level_2') ||
                        item?.types?.includes('administrative_area_level_1')
                )[0]?.short_name,
            });
        }
    }, [data]);

    const getCurrentLocation = () => {
        setAddressSrc('reverseGeolocation');
        setIsChanged(true);
        navigator.geolocation.getCurrentPosition(position => {
            const { longitude, latitude } = position.coords;
            if (refMap.current) {
                refMap.current.panTo({ lng: longitude, lat: latitude });
                refMap.current.setZoom(16);
            }
        });
    };
    useEffect(() => {
        if (refMap?.current) {
            const currCenter = refMap.current.getCenter()?.toJSON();
            if (
                currCenter?.lat !== location.coordinate.lat ||
                currCenter.lng !== location.coordinate.lng
            ) {
                refMap.current.setZoom(16);
                refMap.current.panTo(location.coordinate);
            }
        }
    }, [location.coordinate, refMap?.current]);

    const onPlaceChanged = (result: google.maps.places.PlaceResult) => {
        if (
            result &&
            result?.geometry?.location &&
            result?.formatted_address &&
            result?.address_components
        ) {
            const { lat, lng } = result.geometry.location;
            setAddressSrc('autocomplete');
            if (refMap?.current) {
                refMap.current.setCenter({ lat: lat(), lng: lng() });
                refMap.current.setZoom(16);
            }

            onSetLocation({
                address: result.formatted_address,
                coordinate: { lat: lat(), lng: lng() },
                city: result.address_components?.filter(
                    item =>
                        item?.types?.includes('locality') ||
                        item?.types?.includes('administrative_area_level_2') ||
                        item?.types?.includes('administrative_area_level_1')
                )[0]?.short_name,
            });
        }
    };

    const handleCenterChanged = () => {
        if (!refMap.current) return;
        const newPos = refMap.current.getCenter()?.toJSON();
        if (newPos) {
            setCurrCoords(newPos);
        }
    };

    const handleLoad = useCallback((map: google.maps.Map) => {
        refMap.current = map;
        refMap.current.setCenter(location.coordinate);
        refMap.current.addListener('idle', handleCenterChanged);
        refMap.current.addListener('dragend', () => {
            setIsChanged(true);
            setAddressSrc('reverseGeolocation');
        });
        refMap.current.setZoom(location.address ? 16 : 13);
    }, []);
    return (
        <>
            {/* @ts-ignore */}
            <GoogleMap
                mapContainerStyle={{
                    height: '100%',
                    width: '100%',
                    borderRadius: '8px',
                    border: '2px solid #EAECF0',
                }}
                options={defaultMapOptions}
                mapContainerClassName="map-container"
                onLoad={handleLoad}
            >
                <div
                    style={{
                        width: '100%',
                        padding: '8px',
                    }}
                >
                    <div className={styles.input_group_container}>
                        <AutocompleteComponent
                            onPlaceChanged={onPlaceChanged}
                        />
                    </div>
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: 'calc(50% - 13.5px)',
                        top: 'calc(50% - 49px)',
                    }}
                >
                    <Image
                        src={MarkerPin.src}
                        width={27}
                        height={27}
                        alt="marker"
                    />
                </div>
                <button
                    style={{
                        position: 'absolute',
                        bottom: '16px',
                        right: '8px',
                    }}
                    onClick={getCurrentLocation}
                >
                    <Image
                        src={GeoLocationButton.src}
                        width={GeoLocationButton.width}
                        height={GeoLocationButton.height}
                        alt="geo location button"
                    />
                </button>
            </GoogleMap>
        </>
    );
};
export default Map;
